import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  Mountain, AlertCircle, AlertTriangle, Info, RotateCcw, Calculator, TrendingDown, TrendingUp, Minus, Table2,
} from 'lucide-react';
import {
  DEFAULT_INPUTS, FIELD_SPECS, GRADE_SPECS, OPTIONAL_SPECS, TYPICAL_GRADES, KG_TO_LB, LB_TO_KG, validateField,
} from '../../lib/gradientForceTools';
import { gradientForceToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const ALL_SPECS = [...FIELD_SPECS, ...Object.values(GRADE_SPECS), ...OPTIONAL_SPECS];
const SPEC_BY_KEY = Object.fromEntries(ALL_SPECS.map((s) => [s.key, s]));
const MAX_GRADE = GRADE_SPECS.percent.max;
const INPUT_KEYS = Object.keys(DEFAULT_INPUTS);

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => Math.round(n * 100) / 100;
const pctToDeg = (p) => (Math.atan(p / 100) * 180) / Math.PI;
const degToPct = (d) => Math.tan((d * Math.PI) / 180) * 100;

const DIRECTION_INFO = {
  uphill: {
    label: 'Uphill',
    note: 'Gradient Force opposes forward motion — the vehicle must overcome it to climb the grade.',
    interpretation: 'is the part of the vehicle’s weight pulling it back down the slope. The powertrain must supply at least this much force at the wheels to climb at steady speed.',
    icon: TrendingUp,
    accent: 'text-autoxium-blue',
    badgeClass: 'border-autoxium-blue/40 text-autoxium-blue',
  },
  downhill: {
    label: 'Downhill',
    note: 'Gradient Force acts in the direction of forward motion — it accelerates the vehicle down the grade. Negative torque/power means the brakes or regenerative braking must absorb it to hold speed.',
    interpretation: 'is the part of the vehicle’s weight pushing it down the slope. To hold a steady speed the brakes (or regenerative braking) must absorb this force, minus whatever rolling resistance and drag already provide.',
    icon: TrendingDown,
    accent: 'text-emerald-600',
    badgeClass: 'border-emerald-600/40 text-emerald-600',
  },
  flat: {
    label: 'Flat',
    note: 'A 0% grade has no slope component of weight — Gradient Force is zero.',
    interpretation: 'is zero on a flat road: all of the vehicle’s weight presses straight down onto the tyres.',
    icon: Minus,
    accent: 'text-muted-foreground',
    badgeClass: 'border-structure text-muted-foreground',
  },
};

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid={`gradient-force-tip-${htmlFor}`} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const FieldError = ({ fieldKey, error }) => (error ? (
  <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`gradient-force-${fieldKey}-error`}>
    <AlertCircle className="h-3 w-3 shrink-0" /> {error}
  </p>
) : null);

const initialRaw = () => {
  const out = {};
  INPUT_KEYS.forEach((k) => { out[k] = String(DEFAULT_INPUTS[k]); });
  return out;
};

const ResultRow = ({ label, children, testId }) => (
  <div className="flex items-baseline justify-between gap-3" data-testid={testId}>
    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="font-mono text-sm text-right">{children}</span>
  </div>
);

const GradientForceCalculator = () => {
  const [raw, setRaw] = useState(initialRaw);
  const [gradeUnit, setGradeUnit] = useState('percent');
  const [massUnit, setMassUnit] = useState('kg');
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));
  const gradeSpec = GRADE_SPECS[gradeUnit];

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = { gradeUnit };
    const specs = [...FIELD_SPECS, gradeSpec, ...OPTIONAL_SPECS.filter((s) => raw[s.key] !== '')];
    for (const spec of specs) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [raw, gradeUnit, gradeSpec]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const runCalculation = () => {
    if (hasErrors) return;
    const requestId = ++requestIdRef.current;
    gradientForceToolApi.calculate(values)
      .then((outcome) => {
        if (requestId !== requestIdRef.current) return;
        if (outcome.errors) {
          setResults(null);
          setCalcError(Object.values(outcome.errors)[0]);
        } else {
          setResults(outcome.results);
          setCalcError(null);
        }
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setResults(null);
        setCalcError(e.message);
      });
  };

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }
    const timer = setTimeout(runCalculation, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values]);

  const handleReset = () => {
    setRaw(initialRaw());
    setGradeUnit('percent');
    setMassUnit('kg');
  };

  // Switching the grade unit converts the current value so the slope itself is kept.
  const changeGradeUnit = (unit) => {
    if (unit === gradeUnit) return;
    const n = Number(raw[gradeSpec.key]);
    if (raw[gradeSpec.key] !== '' && !Number.isNaN(n)) {
      if (unit === 'degree') setField('gradeDeg', String(round2(pctToDeg(n))));
      else setField('gradePercent', String(round2(degToPct(n))));
    }
    setGradeUnit(unit);
  };

  const applyTypicalGrade = (percent) => {
    setGradeUnit('percent');
    setField('gradePercent', String(percent));
  };

  // The diagram is driven directly off the raw Road Grade input (not the debounced
  // backend result) so it visibly updates immediately as the user types, per spec.
  // atan/sin of a single percentage is trivial enough to compute client-side.
  const liveGradePercent = useMemo(() => {
    const n = Number(raw[gradeSpec.key]);
    if (raw[gradeSpec.key] === '' || Number.isNaN(n)) return 0;
    const pct = gradeUnit === 'degree' ? degToPct(Math.max(-45, Math.min(45, n))) : n;
    return Math.max(-MAX_GRADE, Math.min(MAX_GRADE, pct));
  }, [raw, gradeSpec.key, gradeUnit]);

  // Mass is always stored in kg; the lb option only changes what is shown/typed.
  const massDisplay = massUnit === 'lb' && raw.vehicleMassKg !== '' && !Number.isNaN(Number(raw.vehicleMassKg))
    ? String(round1(Number(raw.vehicleMassKg) * KG_TO_LB))
    : raw.vehicleMassKg;
  const onMassChange = (e) => {
    const typed = e.target.value;
    if (massUnit === 'lb' && typed !== '' && typed !== '-') {
      const n = Number(typed);
      setField('vehicleMassKg', Number.isNaN(n) ? typed : String(n * LB_TO_KG));
    } else {
      setField('vehicleMassKg', typed);
    }
  };
  const massError = fieldErrors.vehicleMassKg && massUnit === 'lb' && !['Required.', 'Must be a number.'].includes(fieldErrors.vehicleMassKg)
    ? `Must be between ${fmt(SPEC_BY_KEY.vehicleMassKg.min * KG_TO_LB, 0)} and ${fmt(SPEC_BY_KEY.vehicleMassKg.max * KG_TO_LB, 0)} lb.`
    : fieldErrors.vehicleMassKg;

  const dirInfo = results ? DIRECTION_INFO[results.direction] : null;
  const DirIcon = dirInfo?.icon;
  const hasOptional = results && (results.wheelTorqueNm !== null || results.gradePowerKw !== null);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6" aria-label="Gradient Force Calculator">
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="technical-card lg:col-span-3">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Mountain className="h-5 w-5 text-autoxium-blue" />
                Gradient Force Calculator
              </CardTitle>
              <CardDescription>
                Calculate the component of vehicle weight acting along a road gradient —
                commonly called Gradient Force or Grade Resistance.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
                <div className="space-y-2">
                  <FieldLabel htmlFor="gradient-force-mass" unit={massUnit} tip="Total loaded mass: vehicle, driver, passengers and cargo — plus any trailer being towed. Use the gross vehicle (or combination) weight for a worst-case figure.">
                    Vehicle Mass
                  </FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="gradient-force-mass"
                      type="number"
                      step="any"
                      value={massDisplay}
                      onChange={onMassChange}
                      className="font-mono"
                      aria-invalid={!!fieldErrors.vehicleMassKg}
                      data-testid="gradient-force-vehicleMassKg-input"
                    />
                    <Select value={massUnit} onValueChange={setMassUnit}>
                      <SelectTrigger className="w-20 shrink-0" aria-label="Mass unit" data-testid="gradient-force-mass-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="lb">lb</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <FieldError fieldKey="vehicleMassKg" error={massError} />
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="gradient-force-grade" unit={gradeSpec.unit} tip="Road grade = rise ÷ horizontal distance × 100 (not rise ÷ distance driven along the road). Positive = uphill, negative = downhill, 0 = flat. 100 % = 45°.">
                    Road Grade
                  </FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="gradient-force-grade"
                      type="number"
                      step="any"
                      value={raw[gradeSpec.key]}
                      onChange={(e) => setField(gradeSpec.key, e.target.value)}
                      className="font-mono"
                      aria-invalid={!!fieldErrors[gradeSpec.key]}
                      data-testid="gradient-force-grade-input"
                    />
                    <Select value={gradeUnit} onValueChange={changeGradeUnit}>
                      <SelectTrigger className="w-20 shrink-0" aria-label="Grade unit" data-testid="gradient-force-grade-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="degree">°</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <FieldError fieldKey={gradeSpec.key} error={fieldErrors[gradeSpec.key]} />
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="gradient-force-gravity" unit={SPEC_BY_KEY.gravityMs2.unit} tip="Gravitational acceleration. 9.81 m/s² is the usual rounded value (standard gravity is 9.80665). Earth's surface gravity only varies from about 9.78 (equator) to 9.83 (poles).">
                    Gravity
                  </FieldLabel>
                  <Input
                    id="gradient-force-gravity"
                    type="number"
                    step="any"
                    value={raw.gravityMs2}
                    onChange={(e) => setField('gravityMs2', e.target.value)}
                    className="font-mono"
                    aria-invalid={!!fieldErrors.gravityMs2}
                    data-testid="gradient-force-gravityMs2-input"
                  />
                  <FieldError fieldKey="gravityMs2" error={fieldErrors.gravityMs2} />
                </div>
              </div>

              <div className="space-y-3">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Optional — for wheel torque and power
                </p>
                <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
                  <div className="space-y-2">
                    <FieldLabel htmlFor="gradient-force-radius" unit="m" tip="Loaded (dynamic) rolling radius of the driven tyres, e.g. about 0.31 m for a 205/55 R16. Adds the wheel torque needed just to overcome the grade (total across the driven wheels).">
                      Tyre Rolling Radius
                    </FieldLabel>
                    <Input
                      id="gradient-force-radius"
                      type="number"
                      step="any"
                      placeholder="e.g. 0.31"
                      value={raw.wheelRadiusM}
                      onChange={(e) => setField('wheelRadiusM', e.target.value)}
                      className="font-mono"
                      aria-invalid={!!fieldErrors.wheelRadiusM}
                      data-testid="gradient-force-wheelRadiusM-input"
                    />
                    <FieldError fieldKey="wheelRadiusM" error={fieldErrors.wheelRadiusM} />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel htmlFor="gradient-force-speed" unit="km/h" tip="Steady climbing speed. Adds the power needed just to overcome the grade (P = F × v).">
                      Vehicle Speed
                    </FieldLabel>
                    <Input
                      id="gradient-force-speed"
                      type="number"
                      step="any"
                      placeholder="e.g. 40"
                      value={raw.speedKmph}
                      onChange={(e) => setField('speedKmph', e.target.value)}
                      className="font-mono"
                      aria-invalid={!!fieldErrors.speedKmph}
                      data-testid="gradient-force-speedKmph-input"
                    />
                    <FieldError fieldKey="speedKmph" error={fieldErrors.speedKmph} />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="button" onClick={runCalculation} disabled={hasErrors} data-testid="gradient-force-calculate-btn">
                  <Calculator className="h-4 w-4" /> Calculate
                </Button>
                <Button type="button" variant="outline" onClick={handleReset} data-testid="gradient-force-reset-btn">
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <Card className="technical-card ring-1 ring-autoxium-blue/30 h-fit" data-testid="gradient-force-results-card">
              <CardHeader>
                <CardTitle className="font-chivo uppercase tracking-tight text-base">Gradient Force</CardTitle>
                <CardDescription>Grade Resistance</CardDescription>
              </CardHeader>
              <CardContent>
                <div role="status" aria-live="polite">
                  {hasErrors ? (
                    <p className="text-sm text-muted-foreground" data-testid="gradient-force-results-placeholder">
                      Fix the highlighted field to see results.
                    </p>
                  ) : calcError ? (
                    <div className="flex items-center gap-2 text-sm text-destructive" data-testid="gradient-force-calc-error">
                      <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
                    </div>
                  ) : !results ? (
                    <p className="text-sm text-muted-foreground" data-testid="gradient-force-results-loading">
                      Calculating…
                    </p>
                  ) : (
                    <div className="space-y-4" data-testid="gradient-force-results">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Angle (θ)</p>
                          <p className="font-mono font-bold text-lg">{fmt(results.gradientAngleDeg, 2)} <span className="text-sm font-normal text-muted-foreground">°</span></p>
                        </div>
                        <div>
                          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Grade</p>
                          <p className="font-mono font-bold text-lg">{fmt(results.gradePercent, 2)} <span className="text-sm font-normal text-muted-foreground">%</span></p>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-structure">
                        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Gradient Force</p>
                        <p className="font-mono font-black text-2xl text-autoxium-blue" data-testid="gradient-force-value">{fmt(results.gradientForceN, 0)} <span className="text-sm font-normal text-muted-foreground">N</span></p>
                        <p className="font-mono text-sm text-muted-foreground">{fmt(results.gradientForceKn, 3)} kN · {fmt(results.gradientForceLbf, 0)} lbf</p>
                      </div>
                      <div className="pt-3 border-t border-structure space-y-1.5">
                        <ResultRow label="Normal force" testId="gradient-force-normal">{fmt(results.normalForceN, 0)} N</ResultRow>
                        <ResultRow label="Shortcut m·g·G" testId="gradient-force-approx">
                          {fmt(results.approxForceN, 0)} N <span className="text-muted-foreground">(+{fmt(results.approxErrorPercent, 1)} %)</span>
                        </ResultRow>
                        {hasOptional && results.wheelTorqueNm !== null && (
                          <ResultRow label="Wheel torque" testId="gradient-force-torque">{fmt(results.wheelTorqueNm, 0)} N·m</ResultRow>
                        )}
                        {hasOptional && results.gradePowerKw !== null && (
                          <ResultRow label="Grade power" testId="gradient-force-power">{fmt(results.gradePowerKw, 2)} kW</ResultRow>
                        )}
                      </div>
                      {dirInfo && (
                        <div className="pt-3 border-t border-structure space-y-2">
                          <Badge variant="outline" className={`font-mono text-[11px] gap-1 ${dirInfo.badgeClass}`} data-testid="gradient-force-direction-badge">
                            {DirIcon && <DirIcon className="h-3 w-3" />} {dirInfo.label}
                          </Badge>
                          <p className={`text-xs leading-relaxed ${dirInfo.accent}`}>{dirInfo.note}</p>
                        </div>
                      )}
                      {results.warnings?.length > 0 && (
                        <div className="pt-3 border-t border-structure space-y-2" data-testid="gradient-force-warnings">
                          {results.warnings.map((w) => (
                            <p key={w} className="flex gap-1.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {results && dirInfo && (
              <Card className="technical-card">
                <CardContent className="pt-6 space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="gradient-force-interpretation">
                    <span className="font-semibold text-foreground">Gradient Force</span> {dirInfo.interpretation}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="gradient-force-scope-note">
                    This is the grade force only. The total force to climb also includes rolling
                    resistance, aerodynamic drag and any acceleration, and the motor or engine must
                    also cover drivetrain losses — see the Traction Power Calculator for the full total.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Vehicle-on-Slope Diagram</CardTitle>
            <CardDescription>Updates live as the Road Grade changes. Vector lengths are illustrative, not to scale.</CardDescription>
          </CardHeader>
          <CardContent>
            <GradientSlopeDiagram gradePercent={liveGradePercent} maxGrade={MAX_GRADE} />
          </CardContent>
        </Card>

        {results && <GradeReferenceTable results={results} massKg={values.vehicleMassKg} />}
        <TypicalGradesTable onUse={applyTypicalGrade} />

        <Card className="technical-card" data-testid="gradient-force-method">
          <Accordion type="single" collapsible>
            <AccordionItem value="method" className="border-none">
              <CardHeader className="py-0">
                <AccordionTrigger className="hover:no-underline py-4" data-testid="gradient-force-method-toggle">
                  <CardTitle className="font-chivo uppercase tracking-tight text-sm">Calculation Method</CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent className="pt-0 space-y-3 text-xs text-muted-foreground leading-relaxed">
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 1</Badge>
                    Gradient angle: <span className="font-mono text-foreground">θ = atan(Grade % / 100)</span>
                    {' '}(if the grade is entered in degrees, Grade % = tan θ × 100)
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 2</Badge>
                    Gradient force: <span className="font-mono text-foreground">F_gradient = m × g × sin(θ)</span>
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 3</Badge>
                    Normal force: <span className="font-mono text-foreground">F_normal = m × g × cos(θ)</span>
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">optional</Badge>
                    Wheel torque <span className="font-mono text-foreground">T = F_gradient × r</span>;
                    grade power <span className="font-mono text-foreground">P = F_gradient × v</span> (v in m/s = km/h ÷ 3.6)
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">where</Badge>
                    m = vehicle mass (kg), g = gravitational acceleration (m/s²), θ = gradient
                    angle, r = tyre rolling radius (m), v = speed. 1 N = 0.2248 lbf.
                  </div>
                  {results && (
                    <div className="pt-1 font-mono text-[11px] bg-muted/50 rounded-md px-3 py-2 break-all">
                      θ = atan({fmt(results.gradePercent, 2)} / 100) = {fmt(results.gradientAngleDeg, 2)}°
                      &nbsp;·&nbsp; F = {fmt(values.vehicleMassKg, 0)} × {fmt(values.gravityMs2, 2)} × sin({fmt(results.gradientAngleDeg, 2)}°) = {fmt(results.gradientForceN, 1)} N
                    </div>
                  )}
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">shortcut</Badge>
                    Many textbooks use <span className="font-mono text-foreground">F ≈ m × g × Grade % / 100</span> (tan θ in
                    place of sin θ). It overstates the force by (1 / cos θ − 1): under 1 % up to about 14 %
                    grade, but 4.4 % at 30 % grade and 41 % at 45°. This tool always uses the exact sin θ.
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">grade</Badge>
                    Road grade is rise ÷ horizontal distance. A GPS or altimeter log gives rise ÷ distance
                    driven, which is sin θ, not tan θ — the two differ noticeably only above ~15 %.
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">grip</Badge>
                    Whether a vehicle can actually climb is also limited by tyre grip: with all wheels
                    driven it needs μ ≥ tan θ; with one driven axle, roughly μ × (share of weight on that
                    axle) ≥ tan θ.
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
                    Gradient Force is the component of vehicle weight acting parallel to the
                    road surface, opposing uphill motion. On a downhill gradient, this same
                    force acts in the direction of vehicle motion instead of opposing it.
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      </div>
    </TooltipProvider>
  );
};

const TableCard = ({ title, description, children, testId }) => (
  <Card className="technical-card" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
        <Table2 className="h-4 w-4 text-autoxium-blue" /> {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const HEAD_ROW = 'text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent';
const TH = 'text-right h-auto py-2 pr-3 whitespace-nowrap';
const TD = 'text-right py-2 pr-3 whitespace-nowrap';

const GradeReferenceTable = ({ results, massKg }) => (
  <TableCard
    title="Grade Reference"
    testId="gradient-force-grade-table"
    description={`Grade %, angle and gradient force for the current mass (${fmt(massKg, 0)} kg), uphill. The highlighted row is your grade. Rule of thumb: about 98 N per tonne per 1 % of grade — the "m·g·G" shortcut column shows where that stops being accurate.`}
  >
    <Table className="text-xs font-mono min-w-[560px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Grade (%)</TableHead>
          <TableHead className={TH}>Angle (°)</TableHead>
          <TableHead className={TH}>sin θ</TableHead>
          <TableHead className={TH}>Force (N)</TableHead>
          <TableHead className={TH}>m·g·G (N)</TableHead>
          <TableHead className="text-right h-auto py-2 whitespace-nowrap">Shortcut error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.gradeTable.map((r) => (
          <TableRow key={r.gradePercent} className={r.isCurrent ? 'bg-autoxium-blue/5 font-bold' : ''}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{fmt(r.gradePercent, r.gradePercent % 1 ? 2 : 0)}{r.isCurrent ? ' ◂' : ''}</TableCell>
            <TableCell className={TD}>{fmt(r.angleDeg, 2)}</TableCell>
            <TableCell className={TD}>{fmt(r.sinTheta, 4)}</TableCell>
            <TableCell className={TD}>{fmt(r.forceN, 0)}</TableCell>
            <TableCell className={TD}>{fmt(r.approxForceN, 0)}</TableCell>
            <TableCell className="text-right py-2 whitespace-nowrap">+{fmt(r.approxErrorPercent, 1)} %</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const TypicalGradesTable = ({ onUse }) => (
  <TableCard
    title="Typical Road Grades"
    testId="gradient-force-typical-table"
    description="Indicative grades by road type, to help pick a realistic input. Design limits vary by country and design code (e.g. IRC in India, AASHTO in the US) — use the applicable code for design work."
  >
    <Table className="text-xs font-mono min-w-[600px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Setting</TableHead>
          <TableHead className={TH}>Typical Grade</TableHead>
          <TableHead className={TH}>Max Angle</TableHead>
          <TableHead className="text-left h-auto py-2 pr-3">Note</TableHead>
          <TableHead className="text-right h-auto py-2" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {TYPICAL_GRADES.map((g) => (
          <TableRow key={g.setting}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{g.setting}</TableCell>
            <TableCell className={TD}>{g.grade}</TableCell>
            <TableCell className={TD}>{fmt(pctToDeg(g.maxPercent), 1)}°</TableCell>
            <TableCell className="py-2 pr-3 font-sans text-muted-foreground min-w-[180px]">{g.note}</TableCell>
            <TableCell className="text-right py-1">
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => onUse(g.maxPercent)} data-testid={`gradient-force-use-${g.maxPercent}`}>
                Use {g.maxPercent} %
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

// ---------------------------------------------------------------------------
// Vehicle-on-slope diagram (pure SVG, built as a markup string and injected via
// dangerouslySetInnerHTML). This project's dev-only "visual edits" Babel plugin
// (frontend/plugins/visual-edits/babel-metadata-plugin.js) wraps every dynamic JSX
// child of a lowercase element in an HTML <span data-ve-dynamic> — invalid inside
// the SVG namespace, so hand-authored JSX <svg> children (arcs, conditional
// arrows/labels) silently fail to render. Building the SVG as a string sidesteps
// this: dangerouslySetInnerHTML's value is a prop (an object expression), not a
// JSX child, so the plugin never touches it. Safe here because every interpolated
// value is a computed number or one of a few hardcoded label/color strings — never
// user-supplied text.
// ---------------------------------------------------------------------------

const polar = (cx, cy, angleRad, r) => ({ x: cx + r * Math.cos(angleRad), y: cy - r * Math.sin(angleRad) });
const n2 = (v) => v.toFixed(2);

const GradientSlopeDiagram = ({ gradePercent, maxGrade }) => {
  const thetaRad = Math.atan(gradePercent / 100);
  const thetaDeg = (thetaRad * 180) / Math.PI;
  const thetaMaxRad = Math.atan(maxGrade / 100);

  const cx = 200;
  const cy = 150;
  // Shorten the road on steep grades so both ends stay inside the viewBox.
  const roadHalf = Math.min(140, 100 / Math.max(Math.abs(Math.sin(thetaRad)), 1e-6));

  const roadA = polar(cx, cy, thetaRad, roadHalf);
  const roadB = { x: 2 * cx - roadA.x, y: 2 * cy - roadA.y };
  const lowerEnd = roadA.y > roadB.y ? roadA : roadB;
  const upperEnd = roadA.y > roadB.y ? roadB : roadA;

  // The angle arc/label always sits on the "upper" (higher-elevation) side, opposite
  // the Gradient Force arrow (which always points toward lowerEnd) — otherwise the
  // two collide whenever the higher-elevation end happens to fall on the same side
  // as the force arrow (e.g. on a downhill grade).
  const upperIsRoadA = upperEnd === roadA;
  const sideRay = upperIsRoadA ? 0 : Math.PI;
  const roadRay = upperIsRoadA ? thetaRad : thetaRad + Math.PI;
  const bisectorAngle = sideRay + (roadRay - sideRay) / 2;
  const horizontalPoint = polar(cx, cy, sideRay, 44);
  const roadArcPoint = polar(cx, cy, roadRay, 44);
  const arcControl = Math.abs(thetaRad) > 0.001
    ? polar(cx, cy, bisectorAngle, 44 / Math.cos(thetaRad / 2))
    : horizontalPoint;
  const thetaLabelPos = polar(cx, cy, bisectorAngle, 60);
  // Anchor the label away from the road so it never overlaps the road on steep grades.
  const thetaLabelAnchor = upperIsRoadA ? 'start' : 'end';

  const perpAngle = thetaRad + Math.PI / 2;
  const vehicle = polar(cx, cy, perpAngle, 16);

  const weightEnd = { x: vehicle.x, y: vehicle.y + 60 };

  // sqrt keeps the arrow readable on everyday grades now that the range runs to 45°.
  const gradientMag = Math.sqrt(Math.abs(Math.sin(thetaRad)) / Math.max(Math.sin(thetaMaxRad), 1e-6));
  const gradientLen = 22 + 55 * gradientMag;
  const towardLower = { x: (lowerEnd.x - vehicle.x), y: (lowerEnd.y - vehicle.y) };
  const towardLowerMag = Math.hypot(towardLower.x, towardLower.y) || 1;
  const gradientEnd = {
    x: vehicle.x + (towardLower.x / towardLowerMag) * gradientLen,
    y: vehicle.y + (towardLower.y / towardLowerMag) * gradientLen,
  };

  const showGradientArrow = Math.abs(gradePercent) > 0.05;
  const forceColor = gradePercent > 0.05 ? '#1E40AF' : gradePercent < -0.05 ? '#16a34a' : '#64748b';

  const groundPoly = `${n2(roadA.x)},${n2(roadA.y)} ${n2(roadB.x)},${n2(roadB.y)} ${n2(roadB.x)},272 ${n2(roadA.x)},272`;

  const arcMarkup = Math.abs(thetaRad) > 0.005
    ? `<path d="M ${n2(horizontalPoint.x)} ${n2(horizontalPoint.y)} Q ${n2(arcControl.x)} ${n2(arcControl.y)} ${n2(roadArcPoint.x)} ${n2(roadArcPoint.y)}" fill="none" stroke="#1E40AF" stroke-width="1.5" />
       <text x="${n2(thetaLabelPos.x)}" y="${n2(thetaLabelPos.y)}" text-anchor="${thetaLabelAnchor}" font-size="12" font-family="monospace" font-weight="700" fill="#1E40AF">&#952; = ${thetaDeg.toFixed(1)}&#176;</text>`
    : '';

  const showEndLabels = Math.abs(gradePercent) > 0.05;
  const upperLabel = showEndLabels
    ? `<text x="${n2(upperEnd.x)}" y="${n2(upperEnd.y - 10)}" text-anchor="middle" font-size="10" font-family="monospace" fill="#64748b">${gradePercent >= 0 ? 'UPHILL' : 'DOWNHILL'}</text>`
    : '';
  const lowerLabel = showEndLabels
    ? `<text x="${n2(lowerEnd.x)}" y="${n2(lowerEnd.y + 18)}" text-anchor="middle" font-size="10" font-family="monospace" fill="#64748b">${gradePercent >= 0 ? 'DOWNHILL' : 'UPHILL'}</text>`
    : '';

  const forceMarkup = showGradientArrow
    ? `<line x1="${n2(vehicle.x)}" y1="${n2(vehicle.y)}" x2="${n2(gradientEnd.x)}" y2="${n2(gradientEnd.y)}" stroke="${forceColor}" stroke-width="3" marker-end="url(#gf-arrow-force)" />
       <text x="${n2(gradientEnd.x)}" y="${n2(gradientEnd.y + (gradientEnd.y > vehicle.y ? 24 : -14))}" text-anchor="middle" font-size="11" font-family="monospace" font-weight="700" fill="${forceColor}">Gradient Force</text>`
    : '';

  const svgMarkup = `
    <svg viewBox="0 0 400 280" class="w-full h-auto" role="img" aria-label="Vehicle on a ${gradePercent.toFixed(1)}% road grade, angle ${thetaDeg.toFixed(2)} degrees">
      <defs>
        <marker id="gf-arrow-weight" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#64748b" /></marker>
        <marker id="gf-arrow-force" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="${forceColor}" /></marker>
      </defs>
      <polygon points="${groundPoly}" fill="#94a3b8" fill-opacity="0.15" />
      <line x1="${n2(roadA.x)}" y1="${n2(roadA.y)}" x2="${n2(roadB.x)}" y2="${n2(roadB.y)}" stroke="#475569" stroke-width="5" stroke-linecap="round" />
      <line x1="${cx - roadHalf}" y1="${cy}" x2="${cx + roadHalf}" y2="${cy}" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="4 4" />
      ${arcMarkup}
      ${upperLabel}
      ${lowerLabel}
      <g transform="translate(${n2(vehicle.x)} ${n2(vehicle.y)}) rotate(${n2(-thetaDeg)})">
        <rect x="-6" y="-20" width="20" height="9" rx="2" fill="#1E40AF" />
        <rect x="-23" y="-11" width="46" height="14" rx="4" fill="#1E40AF" />
        <circle cx="-13" cy="3" r="6" fill="#1e293b" />
        <circle cx="13" cy="3" r="6" fill="#1e293b" />
      </g>
      <line x1="${n2(vehicle.x)}" y1="${n2(vehicle.y)}" x2="${n2(weightEnd.x)}" y2="${n2(weightEnd.y)}" stroke="#64748b" stroke-width="2.5" marker-end="url(#gf-arrow-weight)" />
      <text x="${n2(weightEnd.x + 8)}" y="${n2(weightEnd.y + 2)}" font-size="11" font-family="monospace" fill="#64748b">Weight (mg)</text>
      ${forceMarkup}
    </svg>
  `;

  return (
    <div
      className="w-full max-w-xl mx-auto"
      data-testid="gradient-force-diagram"
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  );
};

export default GradientForceCalculator;
